import React from "react";
import { Clock, ArrowRight } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
function IndustryInsightsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div data-section-id="31208" className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-[400px] rounded-sm bg-secondary/20 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 text-muted-foreground">
        <p>Unable to load market intelligence at this time.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-12 text-muted-foreground">
        <p>No trading insights available yet.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => {
      const wordCount = post.content ? post.content.replace(/<[^>]*>/g, "").split(/\s+/).length : 0;
      const readingTime = Math.max(1, Math.ceil(wordCount / 200));
      const imageUrl = post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604002/img/generic_insurance_meeting.png";
      return <Link key={post.id} to={post.link || "#"} className="group block h-full">
            <Card data-index={i} className="h-full border border-border/40 rounded-sm shadow-none hover:shadow-[0_0_20px_-5px_rgba(6,182,212,0.3)] hover:border-primary/50 transition-all duration-300 bg-card overflow-hidden flex flex-col">
              {}
              <div className="relative w-full aspect-[4/3] overflow-hidden">
                <img src={imageUrl} alt={post.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-in-out scale-100 group-hover:scale-110" />
                <Badge variant="secondary" className="absolute top-4 right-4 bg-primary text-primary-foreground rounded-sm border-none shadow-lg font-mono text-[10px] flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  {readingTime} MIN READ
                </Badge>
              </div>

              {}
              <CardHeader className="pt-6 pb-2 px-6">
                <div className="flex items-center gap-2 mb-3">
                  {post.categories && post.categories.length > 0 && <span className="text-[10px] font-bold text-primary uppercase tracking-widest font-mono">
                      {post.categories[0].name}
                    </span>}
                  <span className="text-xs text-muted-foreground/50">•</span>
                  <span className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider">
                    {new Date(post.date || "").toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric"
                })}
                  </span>
                </div>
                <CardTitle className="text-xl leading-tight font-bold font-sans tracking-tighter text-foreground group-hover:text-primary transition-colors">
                  {post.title}
                </CardTitle>
              </CardHeader>

              <CardContent className="px-6 pb-6 flex-grow">
                <CardDescription className="line-clamp-3 text-muted-foreground text-sm leading-relaxed">
                  {post.excerpt ? post.excerpt.replace(/<[^>]*>/g, "") : "Access institutional-grade market analysis and strategic trading perspectives..."}
                </CardDescription>
              </CardContent>

              <CardFooter className="px-6 pb-6 pt-0 mt-auto border-t border-border/10 pt-4">
                <span className="text-xs font-bold text-primary font-mono uppercase tracking-widest flex items-center gap-2 group-hover:gap-3 transition-all">
                  Execute Read <ArrowRight className="w-4 h-4" />
                </span>
              </CardFooter>
            </Card>
          </Link>;
    })}
    </div>;
}
export default function IndustryInsights() {
  const wp_query_params = {
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="trading-insights" className="py-20 bg-background dark">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-4xl md:text-5xl font-black text-foreground mb-4 tracking-tighter uppercase">
              Trading Insights
            </h2>
            <p className="text-lg text-muted-silver font-medium leading-relaxed">
              Master the markets with real-time analysis, technical strategies, and institutional-grade reports from our global desk.
            </p>
          </div>
          <Link to="/AcademyPage">
            <Button variant="outline" className="hidden md:flex border-primary/50 text-primary hover:bg-primary/10 rounded-sm font-mono tracking-widest text-xs uppercase px-8">
              View All Reports
            </Button>
          </Link>
        </div>

        <PostsProvider wp_query={{
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <IndustryInsightsConsumer />
        </PostsProvider>

        <div className="mt-10 md:hidden flex justify-center">
          <Link to="/AcademyPage">
            <Button variant="outline" className="w-full sm:w-auto border-primary/50 text-primary hover:bg-primary/10 rounded-sm font-mono tracking-widest text-xs uppercase px-8">
              View All Reports
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}